import { createContext, useContext, useMemo } from 'react'
import type {
  StagedActionEditOptions,
  StagedActionEditTag,
  StagedActionView,
  UpdateStagedActionView,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

interface EditOptionsContextValue {
  editOptions: StagedActionEditOptions | null | undefined
  /**
   * The action's full set of view tags. Each editable field is rendered per
   * tag-group with only its own group's tags, so a field that needs to read a
   * SIBLING tag's current selection (e.g. the When editor reading the selected
   * Calendar) must go through this, not its local `tags` prop.
   */
  viewTags: Array<StagedActionView['tags'][number]>
  /**
   * The toolset that EXECUTES this action (e.g. `google_calendar`,
   * `outlook_calendar`) — the action's `toolSet`. Lets a field resolve
   * provider-specific context: the When editor uses it to query attendee
   * free/busy under a matching-provider calendar connection rather than the
   * user's global-primary one. (NB: not `sourceIntegration`, which is the
   * source-context provenance and is often empty on calendar actions.)
   */
  toolSet?: string
  isEditOptionsLoading: boolean
  isUpdating: boolean
  canEdit: boolean
  // May return the mutation promise (rejects on PATCH failure); consumers can
  // observe rejection to revert optimistic state.
  onUpdate: (view: UpdateStagedActionView) => void | Promise<unknown>
}

const EditOptionsContext = createContext<EditOptionsContextValue | null>(null)

export const EditOptionsProvider = EditOptionsContext.Provider

export function useEditOptions(): EditOptionsContextValue {
  const ctx = useContext(EditOptionsContext)
  if (!ctx) {
    throw new Error('useEditOptions must be used within an EditOptionsProvider')
  }
  return ctx
}

export function useEditTag(key: string): StagedActionEditTag | null {
  const { editOptions } = useEditOptions()
  return useMemo(() => editOptions?.tags?.find((t) => t.key === key) ?? null, [editOptions, key])
}
